import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ConsentElementView from "./ConsentElementView";
import { localized, previewSheets } from "./consentElements";
import { contentBlocksOf, designForPreview } from "./consentSimpleModel";
import { SAMPLE_CONTEXT } from "./consentFields";
import {
  consentFieldPreviewElements,
  consentFieldsOf,
} from "./consentSpecificFields";

const LEADING_TYPES = [
  "clinicLogo",
  "clinicInfo",
  "image",
  "patientInfo",
  "appointmentInfo",
  "doctorInfo",
  "heading",
];

const withFieldLines = (sheets, fieldLines) => {
  if (!fieldLines.length || !sheets.length) return sheets;
  const [first = [], ...rest] = sheets;
  const leading = first.findIndex((element) => !LEADING_TYPES.includes(element.type));
  const at = leading < 0 ? first.length : leading;
  return [[...first.slice(0, at), ...fieldLines, ...first.slice(at)], ...rest];
};

const ZOOMS = [0.6, 0.75, 1, 1.25];

const ConsentPreviewDialog = ({
  open,
  onClose,
  template,
  design,
  lang = "en",
  contentOnly = false,
}) => {
  const [zoom, setZoom] = useState(0.75);
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(true);

  const fieldLines = consentFieldPreviewElements(consentFieldsOf(design));
  const documentSheets = withFieldLines(
    previewSheets(designForPreview(design)),
    fieldLines
  );
  const contentBlocks = contentOnly ? contentBlocksOf(design, lang) : [];
  const contentElement = {
    id: "admin-content-preview",
    type: "richText",
    content: { en: "", hi: "" },
    props: { format: "blocks", blocks: contentBlocks },
  };
  const sheets = contentOnly
    ? contentBlocks.length || fieldLines.length
      ? [[...fieldLines, ...(contentBlocks.length ? [contentElement] : [])]]
      : [[]]
    : documentSheets;
  const current = Math.min(page, sheets.length - 1);
  const visible = showAll ? sheets : [sheets[current]];

  const title =
    localized(
      { en: template?.displayName || template?.name, hi: template?.hindiName },
      lang
    ) || template?.name;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">
            {contentOnly ? "Consent Content" : `Preview — ${title}`}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
            {contentOnly
              ? "Content-only preview"
              : `A4 · sample patient data · ${sheets.length} page${sheets.length === 1 ? "" : "s"}`}
          </Typography>
        </Box>

        <TextField
          select
          size="small"
          label="Zoom"
          sx={{ width: 104 }}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
        >
          {ZOOMS.map((option) => (
            <MenuItem key={option} value={option}>
              {Math.round(option * 100)}%
            </MenuItem>
          ))}
        </TextField>

        <IconButton onClick={onClose} aria-label="Close preview">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ background: "#f1f2f6" }}
        className="consent-preview-body"
      >
        <Box className={contentOnly ? "consent-content-only-screen" : undefined}>
          {visible.map((sheet, index) => (
            <Box key={showAll ? index : current}>
              {!contentOnly ? (
                <Typography className="consent-sheet__label">
                  Page {(showAll ? index : current) + 1} of {sheets.length}
                </Typography>
              ) : null}
              <Box
                className={`consent-sheet${contentOnly ? " consent-sheet--content-only" : ""}`}
                style={{ transform: `scale(${zoom})` }}
                data-testid={`preview-sheet-${(showAll ? index : current) + 1}`}
              >
                {(sheet || []).map((element) => (
                  <ConsentElementView
                    key={element.id}
                    element={element}
                    lang={lang}
                    mode="preview"
                    values={SAMPLE_CONTEXT}
                  />
                ))}
                {!(sheet || []).length ? (
                  <Typography variant="body2" sx={{ color: "#9e9e9e", textAlign: "center", py: 6 }}>
                    No consent content yet.
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>

        {contentOnly ? (
          <Box className="consent-preview-print-only" aria-hidden="true">
            {documentSheets.map((sheet, index) => (
              <Box key={index}>
                <Box className="consent-sheet" data-testid={`print-sheet-${index + 1}`}>
                  {(sheet || []).map((element) => (
                    <ConsentElementView
                      key={element.id}
                      element={element}
                      lang={lang}
                      mode="preview"
                      values={SAMPLE_CONTEXT}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button size="small" onClick={() => setShowAll((value) => !value)}>
          {showAll ? "One page at a time" : "Show all pages"}
        </Button>
        {!showAll ? (
          <>
            <Button
              size="small"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
              {current + 1} / {sheets.length}
            </Typography>
            <Button
              size="small"
              disabled={current >= sheets.length - 1}
              onClick={() => setPage(current + 1)}
            >
              Next
            </Button>
          </>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => window.print()}>Print preview</Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentPreviewDialog;
